/**
 * Form helpers for create/edit partner views.
 */
import type { AddressSelectionResult } from '@/types/geography.types';
import type {
  CreatePartnerRequest,
  Partner,
  UpdatePartnerRequest,
  PartnerFormValues,
} from '@/types/partner.types';
import { resolveStoredDepartmentCode } from '@/utils/department-lookup-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { getCompanyCountry } from '@/stores/themeStore';

/** Empty string → omit from the DTO. */
function trimOrUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed || undefined;
}

/** Empty create form; edit hydrates later via {@link partnerToFormData}. */
export function emptyCreatePartnerForm(): PartnerFormValues {
  return {
    name: '',
    description: '',
    address: '',
    zipCode: '',
    city: '',
    country: getCompanyCountry(),
    department: '',
    website: '',
    contactEmail: '',
    contactPhone: '',
  };
}

/** Hydrate the form from GET partner. {@link Select} uses a department code. */
export function partnerToFormData(partner: Partner): PartnerFormValues {
  return {
    name: partner.name || '',
    description: partner.description || '',
    address: partner.address || '',
    zipCode: partner.zipCode || '',
    city: partner.city || '',
    country: partner.country || getCompanyCountry(),
    department: resolveStoredDepartmentCode(partner.department, partner.zipCode),
    website: partner.website || '',
    contactEmail: partner.contactEmail || '',
    contactPhone: partner.contactPhone || '',
  };
}

/** Patch one string field. */
export function applyPartnerFormField(
  form: PartnerFormValues,
  field: keyof PartnerFormValues,
  value: string,
): PartnerFormValues {
  return { ...form, [field]: value };
}

/** Fill address / city / department from an autocomplete pick. */
export function applyPartnerAddressSelection(
  form: PartnerFormValues,
  result: AddressSelectionResult,
): PartnerFormValues {
  return {
    ...form,
    address: result.addressLine1,
    zipCode: result.postalCode || '',
    city: result.city || '',
    country: result.country || getCompanyCountry(),
    department: result.departmentCode || '',
  };
}

/** Shared create/update payload; blank optional fields are omitted. */
function buildPartnerPayload(form: PartnerFormValues): CreatePartnerRequest {
  return {
    name: form.name.trim(),
    description: trimOrUndefined(form.description),
    address: trimOrUndefined(form.address),
    zipCode: trimOrUndefined(form.zipCode),
    city: trimOrUndefined(form.city),
    country: trimOrUndefined(form.country),
    department: trimOrUndefined(form.department),
    website: trimOrUndefined(form.website),
    contactEmail: trimOrUndefined(form.contactEmail),
    contactPhone: trimOrUndefined(form.contactPhone),
  };
}

/** Create DTO (same payload as update). */
export function buildCreatePartnerDto(form: PartnerFormValues): CreatePartnerRequest {
  return buildPartnerPayload(form);
}

/** Update DTO (same payload as create). */
export function buildUpdatePartnerDto(form: PartnerFormValues): UpdatePartnerRequest {
  return buildPartnerPayload(form);
}

/** Name is the only required field. */
export function isPartnerFormValid(form: PartnerFormValues): boolean {
  return !!form.name.trim();
}

/** Central error copy from {@link getErrorMessage}. */
export function getPartnerApiError(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}
